// ============================================
// FILE 13: /frontend/src/pages/SavedListingsPage.jsx
// PURPOSE: User's saved/bookmarked listings page
// ============================================

import { useState, useEffect } from 'react';
import { Bookmark, Loader2, Trash2, Edit3 } from 'lucide-react';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { useToast } from '../hooks/useToast';
import { useAuth } from '../hooks/useAuth';
import { bookmarksAPI } from '../api/bookmarks.api';
import { useNavigate } from 'react-router-dom';

export default function SavedListingsPage() {
  const { user, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [bookmarks, setBookmarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingBookmark, setEditingBookmark] = useState(null);
  const [editNotes, setEditNotes] = useState('');

  useEffect(() => {
    if (user) {
      fetchBookmarks();
    }
  }, [user]);

  const fetchBookmarks = async () => {
    try {
      const response = await bookmarksAPI.getUserBookmarks(user.id);
      setBookmarks(response.bookmarks);
    } catch (error) {
      console.error('Failed to fetch bookmarks:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load saved listings.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (bookmarkId) => {
    try {
      await bookmarksAPI.deleteBookmark(bookmarkId);
      setBookmarks(bookmarks.filter((b) => b.id !== bookmarkId));
      toast({
        title: 'Removed',
        description: 'Listing removed from saved items.',
      });
    } catch (error) {
      console.error('Failed to delete bookmark:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to remove listing.',
      });
    }
  };

  const handleEditNotes = (bookmark) => {
    setEditingBookmark(bookmark);
    setEditNotes(bookmark.notes || '');
  };

  const handleSaveNotes = async () => {
    try {
      await bookmarksAPI.updateBookmark(editingBookmark.id, editNotes);
      setBookmarks(
        bookmarks.map((b) =>
          b.id === editingBookmark.id ? { ...b, notes: editNotes } : b
        )
      );
      toast({
        title: 'Updated',
        description: 'Notes updated successfully.',
      });
      setEditingBookmark(null);
    } catch (error) {
      console.error('Failed to update notes:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to update notes.',
      });
    }
  };

  if (authLoading || loading) {
    return (
      <div className="flex flex-col min-h-screen">
        <Header />
        <main className="flex-grow flex items-center justify-center">
          <Loader2 className="w-6 h-6 sm:w-8 sm:h-8 animate-spin" />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-grow container py-6 sm:py-8 md:py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <Card>
            <CardHeader className="p-4 sm:p-5 md:p-6">
              <CardTitle className="flex items-center gap-2 text-lg sm:text-xl md:text-2xl">
                <Bookmark className="w-5 h-5 sm:w-6 sm:h-6" />
                Saved Listings ({bookmarks.length})
              </CardTitle>
            </CardHeader>

            <CardContent className="p-3 sm:p-4 md:p-6">
              {bookmarks.length === 0 ? (
                <div className="text-center py-8 sm:py-12">
                  <Bookmark className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-muted-foreground mb-3 sm:mb-4" />
                  <h3 className="text-base sm:text-lg font-semibold mb-1.5 sm:mb-2">
                    No Saved Listings
                  </h3>
                  <p className="text-xs sm:text-sm text-muted-foreground mb-3 sm:mb-4">
                    Start saving listings to keep track of your favorites
                  </p>
                  <Button onClick={() => navigate('/vehicles')} className="h-9 sm:h-10 text-xs sm:text-sm">
                    Browse Listings
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                  {bookmarks.map((bookmark) => (
                    <Card key={bookmark.id} className="overflow-hidden">
                      <div
                        onClick={() => navigate(`/listing/${bookmark.listing.id}`)}
                        className="cursor-pointer"
                      >
                        {/* Image */}
                        <div className="w-full h-40 sm:h-48 bg-gray-200">
                          {bookmark.listing.imageUrl?.[0] ? (
                            <img
                              src={bookmark.listing.imageUrl[0]}
                              alt={bookmark.listing.title}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs sm:text-sm">
                              No Image
                            </div>
                          )}
                        </div>

                        {/* Content */}
                        <div className="p-3 sm:p-4">
                          <h3 className="font-semibold text-sm sm:text-base md:text-lg line-clamp-1">
                            {bookmark.listing.title}
                          </h3>
                          <p className="text-primary font-bold text-base sm:text-lg md:text-xl mt-1 sm:mt-2">
                            KES {parseFloat(bookmark.listing.price).toLocaleString()}
                          </p>
                          <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                            {bookmark.listing.county}
                          </p>

                          {/* Notes */}
                          {bookmark.notes && (
                            <div className="mt-2 sm:mt-3 p-2 bg-gray-50 rounded text-xs sm:text-sm">
                              <p className="text-muted-foreground italic line-clamp-2">
                                {bookmark.notes}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="border-t px-3 sm:px-4 py-2 sm:py-3 flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditNotes(bookmark)}
                          className="flex-1 h-8 sm:h-9 text-xs sm:text-sm"
                        >
                          <Edit3 className="w-3 h-3 sm:w-4 sm:h-4 mr-1.5" />
                          {bookmark.notes ? 'Edit Note' : 'Add Note'}
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDelete(bookmark.id)}
                          className="h-8 sm:h-9 text-xs sm:text-sm"
                        >
                          <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />

      {/* Edit Notes Dialog */}
      <Dialog
        open={!!editingBookmark}
        onOpenChange={() => setEditingBookmark(null)}
      >
        <DialogContent className="max-w-md mx-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg">Edit Notes</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 sm:space-y-4 mt-3 sm:mt-4">
            <Textarea
              value={editNotes}
              onChange={(e) => setEditNotes(e.target.value)}
              placeholder="Add your notes here..."
              rows={4}
              className="text-xs sm:text-sm resize-none"
            />
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setEditingBookmark(null)}
                className="flex-1 h-9 sm:h-10 text-xs sm:text-sm"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveNotes}
                className="flex-1 h-9 sm:h-10 text-xs sm:text-sm"
              >
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
